import { compactParams, deleteRequest, getData, postData, putData } from './api';
import {
  Contact,
  ContactVersion,
  CreateContactDTO,
  UpdateContactDTO,
  ContactListParams,
  ContactListResponse,
  ContactSegmentCounts,
  ImportPreview,
  ImportResult,
  MergeContactsDTO,
  MergeContactsResult,
} from '../types/contact';

export const contactApi = {
  getMe: (): Promise<Contact> => getData<Contact>('/api/contacts/me'),

  create: (data: CreateContactDTO): Promise<Contact> => postData<Contact, CreateContactDTO>('/api/contacts', data),

  list: (params?: ContactListParams): Promise<ContactListResponse> => {
    const queryParams = compactParams({
      skip: params?.skip,
      take: params?.take,
      search: params?.search,
      sortBy: params?.sortBy,
      sortOrder: params?.sortOrder,
      segment: params?.segment,
      tagIds: params?.tagIds?.join(','),
      excludeColdInbound: params?.excludeColdInbound,
      company: params?.company,
      hasEmail: params?.hasEmail,
      hasPhone: params?.hasPhone,
    });

    return getData<ContactListResponse>('/api/contacts', { params: queryParams });
  },

  getSegmentCounts: (params?: { excludeColdInbound?: boolean }): Promise<ContactSegmentCounts> =>
    getData<ContactSegmentCounts>('/api/contacts/segments', {
      params: params?.excludeColdInbound !== undefined
        ? { excludeColdInbound: params.excludeColdInbound }
        : undefined,
    }),

  get: (id: string): Promise<Contact> => getData<Contact>(`/api/contacts/${id}`),

  update: (id: string, data: UpdateContactDTO): Promise<Contact> =>
    putData<Contact, UpdateContactDTO>(`/api/contacts/${id}`, data),

  delete: (id: string): Promise<void> => deleteRequest(`/api/contacts/${id}`),

  getVersions: (id: string): Promise<ContactVersion[]> =>
    getData<ContactVersion[]>(`/api/contacts/${id}/versions`),

  getVersion: (id: string, version: number): Promise<ContactVersion> =>
    getData<ContactVersion>(`/api/contacts/${id}/versions/${version}`),

  restoreVersion: (id: string, version: number): Promise<Contact> =>
    postData<Contact>(`/api/contacts/${id}/restore/${version}`),

  previewImport: (file: File): Promise<ImportPreview> => {
    const formData = new FormData();
    formData.append('file', file);

    return postData<ImportPreview, FormData>('/api/contacts/import/preview', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  importFromVCard: (file: File, skipDuplicates: boolean = true): Promise<ImportResult> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('skipDuplicates', String(skipDuplicates));

    return postData<ImportResult, FormData>('/api/contacts/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },

  merge: (data: MergeContactsDTO): Promise<MergeContactsResult> =>
    postData<MergeContactsResult, MergeContactsDTO>('/api/contacts/merge', data),

  findDuplicates: (id: string): Promise<Contact[]> =>
    getData<Contact[]>(`/api/contacts/${id}/duplicates`),
};

export default contactApi;
